import OnboardingHeader from '@/components/Onboarding/OnboardingHeader';
import OnboardingWrapper from '@/components/Onboarding/OnboardingWrapper';
import { useAuth } from '@/context/AuthContext';
import { motion } from 'motion/react';
import { signIn } from 'next-auth/react';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { Button } from '../Button/Button';
import { LoadingSpinner } from '../Loading';
import { GoogleIcon } from '../TsIcons';
import EmailForm from './EmailForm';
import OnboardingFooter from './OnboardingFooter';
import OtpForm from './OtpForm';

type FullPageAuthProps = {
  showLogo?: boolean;
  callbackUrl?: string;
};

const FullPageAuth = ({
  showLogo = true,
  callbackUrl: _callbackUrl,
}: FullPageAuthProps) => {
  const [isEmaiLoading, setIsEmailLoading] = useState<boolean>(false);
  const [email, setEmail] = useState<string | null>(null);
  const router = useRouter();
  const { userLoading, user } = useAuth();
  const callbackUrl = _callbackUrl ?? (router.query.callbackUrl as string);

  useEffect(() => {
    if (user) router.push('/new');
  }, [user]);

  if (userLoading || user)
    return (
      <div className="flex w-full items-center justify-center">
        <LoadingSpinner variant="screen" color={undefined} />
      </div>
    );

  return (
    <motion.div
      className="flex flex-col h-full items-center justify-center"
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
    >
      {showLogo && (
        <OnboardingHeader
          onBack={() => {
            if (email === null) router.push('/');
            setEmail(null);
            setIsEmailLoading(false);
          }}
          disabled={undefined}
        />
      )}
      <div className="w-80 flex flex-col items-stretch">
        <OnboardingWrapper
          heading={'Log in or sign up'}
          subHeading={
            email &&
            `We sent a temporary log in code. Check your inbox at ${email}`
          }
          email={email}
        >
          {!email && (
            <>
              <Button
                className="hover:scale-[1.02] transition-all"
                variant="secondary"
                size="lg"
                onClick={() => {
                  signIn('google', {
                    callbackUrl: callbackUrl
                      ? `${callbackUrl}?checkDevice=true`
                      : '/new?checkDevice=true',
                  });
                }}
              >
                <GoogleIcon />
                Continue with Google
              </Button>
              <div className="my-3 w-full flex flex-row items-center px-1 ">
                <div className="h-0.5 w-full bg-border1" />
                <div className="bg-white mx-4 text-sm text-textGray">or</div>
                <div className="h-0.5 w-full bg-border1" />
              </div>
            </>
          )}
          {email && (
            <OtpForm
              email={email}
              callbackUrl={callbackUrl ?? '/new?checkDevice=true'}
            />
          )}
          {!email && (
            <EmailForm
              isLoading={isEmaiLoading}
              onSubmit={async ({ email }) => {
                setIsEmailLoading(true);
                const res = await signIn('resend', {
                  email,
                  redirect: false,
                  callbackUrl: callbackUrl
                    ? `${callbackUrl}?checkDevice=true`
                    : '/new?checkDevice=true',
                });
                if (res?.error) {
                  setIsEmailLoading(true);
                  if (res?.url) {
                    window.location.replace(res.url);
                  }
                } else {
                  setEmail(email);
                }
              }}
            />
          )}
        </OnboardingWrapper>
      </div>

      <OnboardingFooter variant="legal" />
    </motion.div>
  );
};

export default FullPageAuth;
